import * as wjcCore from '@mescius/wijmo';
import {Component, Input, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ToasterService} from 'angular2-toaster';
import {DataService, OrientDBCollectionView} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {TranslocoService} from '@jsverse/transloco';

interface InvoiceOption {
    invoiceRid: string;
    customerRid: string;
    customerName: string;
    total: number;
}

/**
 * kobe-ac 請求編集 (Issue #174)。売買台帳PDFの生成元である AC_Invoice_Head / AC_Invoice_Line を
 * FlexGrid で表示・編集し、その行から (AC_Item を再構築せず) PDF を再作成する。
 *
 * <p>オークション → 顧客 (その日に請求のある顧客) を選ぶと Head/Line を編集可能グリッドに読み込む。
 * 「変更保存」で編集を永続化し、「PDF再作成」で /rest/kobeac/ledger/render を呼び<b>現在の行</b>から
 * 売買台帳PDFを描画する。<b>注意</b>: 元の AC_Item を編集すると Hook が請求を再構築し、この手編集は
 * 上書きされる (AC_Item がソースオブトゥルース)。
 */
@Component({
    standalone: false,
    selector: 'kobeac-invoice-edit',
    templateUrl: 'invoice-edit.component.html',
})
export class KobeacInvoiceEditComponent implements OnInit {
    private base = ActivePromotionConfig.SERVER_URL + '/rest/kobeac/ledger';
    auctions: any[] = [];
    invoices: InvoiceOption[] = [];

    selectedAuctionRid: string = '';
    selectedInvoiceRid: string = '';

    heads: OrientDBCollectionView | null = null;
    lines: OrientDBCollectionView | null = null;
    readonly = true;
    rendering = false;

    /** 統合画面 (kobeac-ledger-home) 内に埋め込むと true。自前のオークション/顧客セレクタを
     *  隠し、対象は親から auctionRid / customerRid で受け取る。単独ルートでは false のまま。 */
    @Input() embedded = false;
    /** 顧客一覧 (invoices) ロード前に受けた customerRid を保持し、ロード後に適用する。 */
    private pendingCustomerRid: string | null = null;

    @Input() set auctionRid(rid: string) {
        const v = rid || '';
        if (v === this.selectedAuctionRid) {
            return;
        }
        this.selectedAuctionRid = v;
        this.onAuctionChange();
    }

    @Input() set customerRid(rid: string) {
        this.applyCustomer(rid || '');
    }

    /** 親から渡された顧客 (@rid) に対応する請求を選び Head/Line を読む。
     *  invoices 未ロードなら pending に退避し onAuctionChange 完了後に適用する。 */
    private applyCustomer(customerRid: string): void {
        if (!customerRid) {
            return;
        }
        if (!this.invoices.length) {
            this.pendingCustomerRid = customerRid;
            return;
        }
        const found = this.invoices.filter((inv) => inv.customerRid === customerRid);
        if (found.length) {
            if (this.selectedInvoiceRid !== found[0].invoiceRid) {
                this.selectedInvoiceRid = found[0].invoiceRid;
                this.onInvoiceChange();
            }
        } else {
            // その顧客はこの開催日に請求が無い。
            this.selectedInvoiceRid = '';
            this.heads = null;
            this.lines = null;
        }
    }

    constructor(private dataService: DataService, private kobeac: KobeacService,
                private http: HttpClient, private toaster: ToasterService, private transloco: TranslocoService) {
    }

    async ngOnInit(): Promise<void> {
        await this.kobeac.ensureLoaded();
        this.auctions = this.kobeac.auctions;
    }

    auctionLabel(a: any): string {
        return this.kobeac.formatOrientDate(a['日付']);
    }

    /** 現在選択中の請求 (顧客) 情報。 */
    get currentInvoice(): InvoiceOption | null {
        for (const inv of this.invoices) {
            if (inv.invoiceRid === this.selectedInvoiceRid) {
                return inv;
            }
        }
        return null;
    }

    /** オークションを選んだら、その日に請求のある顧客一覧を読む。 */
    onAuctionChange(): void {
        this.invoices = [];
        this.selectedInvoiceRid = '';
        this.heads = null;
        this.lines = null;
        if (!this.selectedAuctionRid) {
            return;
        }
        const sql = 'SELECT @rid as invoiceRid, customer, customerName, total FROM AC_Invoice WHERE auction = '
            + this.selectedAuctionRid + ' ORDER BY customerName';
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:0'}, ActivePromotionConfig.SERVER_URL).subscribe((res: any) => {
            const rows = wjcCore.isArray(res.results) ? res.results : [];
            this.invoices = rows.map((r: any) => ({
                invoiceRid: this.ridOf(r.invoiceRid),
                customerRid: this.ridOf(r.customer),
                customerName: r.customerName,
                total: r.total,
            })).filter((r: InvoiceOption) => r.invoiceRid && r.customerRid);
            // 埋め込み時: 顧客一覧ロード前に受けた選択顧客をここで適用する。
            if (this.pendingCustomerRid) {
                const c = this.pendingCustomerRid;
                this.pendingCustomerRid = null;
                this.applyCustomer(c);
            }
        }, () => this.toaster.pop('error', this.transloco.translate('kobeac.invoice'), this.transloco.translate('kobeac.customer_list_failed')));
    }

    /** 顧客 (請求) を選んだら Head/Line を編集可能グリッドに読む。 */
    onInvoiceChange(): void {
        this.heads = null;
        this.lines = null;
        this.readonly = true;
        if (!this.selectedInvoiceRid) {
            return;
        }
        this.heads = this.buildCv('AC_Invoice_Head');
        this.lines = this.buildCv('AC_Invoice_Line');
    }

    private buildCv(className: string): OrientDBCollectionView {
        const sql = 'SELECT FROM ' + className + ' WHERE invoice = ' + this.selectedInvoiceRid + ' ORDER BY sort';
        const cv = new OrientDBCollectionView(this.dataService, ActivePromotionConfig.DATABASE,
            sql, className, '', 0, 1000, true, true, false);
        cv.url = ActivePromotionConfig.SERVER_URL;
        cv.tokenName = ActivePromotionConfig.DATABASE + '_token';
        cv.fetchPlan = '*:0';
        // OrientDBCollectionView は filterOnServer=true のときだけ実際にクエリを投げる
        // (_queryData の入口が if(filterOnServer))。url/tokenName/fetchPlan を設定した後に
        // true にして読み込みを起動する (他画面と同じ流儀)。WHERE は base query 側に持たせる。
        cv.filterOnServer = true;
        return cv;
    }

    hasChanges(): boolean {
        return !!((this.heads && this.heads.hasChanges()) || (this.lines && this.lines.hasChanges()));
    }

    /**
     * 変更を永続化し、保存完了後に自動で PDF を再作成する (AC_Invoice.total も再計算される)。
     * OrientDBCollectionView の保存は非同期なので saveSucceeded/saveFailed を待って render へ繋ぐ。
     * 1 CV は「更新(追加+編集)」「削除」で最大2バッチを投げるため、待つバッチ数を保存前に数える
     * (saveChanges が itemsAdded/Edited/Removed をクリアするため)。
     */
    saveChanges(): void {
        if (!this.hasChanges()) {
            this.toaster.pop('info', this.transloco.translate('common.save'), this.transloco.translate('kobeac.no_changes'));
            return;
        }
        const cvs = [this.heads, this.lines].filter((cv): cv is OrientDBCollectionView => !!cv && cv.hasChanges());
        let pending = 0;
        for (const cv of cvs) {
            if (cv.itemsAdded.length > 0 || cv.itemsEdited.length > 0) {
                pending++;
            }
            if (cv.itemsRemoved.length > 0) {
                pending++;
            }
        }
        if (pending === 0) {
            this.toaster.pop('info', this.transloco.translate('common.save'), this.transloco.translate('kobeac.no_changes'));
            return;
        }
        this.rendering = true;
        let settled = false;
        const handlers: {cv: OrientDBCollectionView; ok: any; ng: any}[] = [];
        const cleanup = () => {
            for (const h of handlers) {
                h.cv.saveSucceeded.removeHandler(h.ok);
                h.cv.saveFailed.removeHandler(h.ng);
            }
        };
        const onOk = () => {
            if (settled) {
                return;
            }
            pending--;
            if (pending <= 0) {
                settled = true;
                cleanup();
                // 保存済みの Head/Line から PDF を再作成し、AC_Invoice.total を再計算 (core 側)。
                this.doRender(true, true);
            }
        };
        const onNg = () => {
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            this.rendering = false;
            this.toaster.pop('error', this.transloco.translate('common.save'), this.transloco.translate('kobeac.save_failed_period'));
        };
        for (const cv of cvs) {
            const ok = () => onOk();
            const ng = () => onNg();
            handlers.push({cv, ok, ng});
            cv.saveSucceeded.addHandler(ok);
            cv.saveFailed.addHandler(ng);
        }
        for (const cv of cvs) {
            cv.saveChanges();
        }
    }

    cancelChanges(): void {
        if (this.heads) {
            this.heads.cancelChanges();
        }
        if (this.lines) {
            this.lines.cancelChanges();
        }
    }

    /** 「PDF再作成」ボタン: 現在の (保存済み) Head/Line から台帳PDFを再作成する。 */
    render(): void {
        if (this.hasChanges()) {
            this.toaster.pop('warning', this.transloco.translate('kobeac.rerender_pdf'), this.transloco.translate('kobeac.save_changes_first'));
            return;
        }
        this.rendering = true;
        this.doRender(true, false);
    }

    /**
     * 現在の (保存済みの) Head/Line から売買台帳PDFを再作成する (AC_Item から再構築しない)。
     * core 側の /render は AC_Invoice.total も Head.total 合計で再計算・永続化する。
     * @param reloadInvoices 完了後に AC_Invoice を読み直し、合計 (対象ラベル/ドロップダウン) を更新する。
     * @param fromSave       変更保存からの連鎖呼び出しか (トースト文言の出し分け用)。
     * 呼び出し側で this.rendering=true にしておくこと (保存待ちの間もボタンを無効化するため)。
     */
    private doRender(reloadInvoices: boolean, fromSave: boolean): void {
        const inv = this.currentInvoice;
        if (!inv || !this.selectedAuctionRid) {
            this.rendering = false;
            return;
        }
        const customerRid = inv.customerRid;
        const customerName = inv.customerName;
        this.http.post(this.base + '/render', {auction: this.selectedAuctionRid, customer: customerRid}).subscribe(() => {
            this.rendering = false;
            this.toaster.pop('info', this.transloco.translate('kobeac.rerender_pdf'),
                this.transloco.translate(fromSave ? 'kobeac.saved_and_rerendered' : 'kobeac.rerendered', {name: customerName}));
            if (reloadInvoices) {
                this.reloadInvoices();
            }
        }, () => {
            this.rendering = false;
            this.toaster.pop('error', this.transloco.translate('kobeac.rerender_pdf'), this.transloco.translate('kobeac.rerender_failed'));
        });
    }

    /** AC_Invoice を読み直して合計 (total) を最新化する。選択中の請求/顧客は維持する。 */
    private reloadInvoices(): void {
        if (!this.selectedAuctionRid) {
            return;
        }
        const keep = this.selectedInvoiceRid;
        const sql = 'SELECT @rid as invoiceRid, customer, customerName, total FROM AC_Invoice WHERE auction = '
            + this.selectedAuctionRid + ' ORDER BY customerName';
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:0'}, ActivePromotionConfig.SERVER_URL).subscribe((res: any) => {
            const rows = wjcCore.isArray(res.results) ? res.results : [];
            this.invoices = rows.map((r: any) => ({
                invoiceRid: this.ridOf(r.invoiceRid),
                customerRid: this.ridOf(r.customer),
                customerName: r.customerName,
                total: r.total,
            })).filter((r: InvoiceOption) => r.invoiceRid && r.customerRid);
            this.selectedInvoiceRid = keep;
        });
    }

    private ridOf(v: any): string {
        if (v == null) {
            return '';
        }
        if (wjcCore.isObject(v) && v['@rid']) {
            return v['@rid'];
        }
        const s = String(v).trim();
        return s.indexOf('#') === 0 ? s : '';
    }
}
